import { apiDelete, apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';
import {
  entityDetailKey,
  entityListKey,
  type EntityListKey,
} from '../../../shared/api/entityQueryKeys.js';
import type { InheritedLink } from '../../../shared/ui/LinkCell.js';
import type { DealSalesHealth } from '../../../sales-health/scoring.js';
import type { DealWinProbability } from '../../../sales-health/win-probability.js';
import type {
  NbaActionMeta,
  NbaActionType,
  NbaExecuteMode,
  NbaReasonCode,
} from '../../../sales-health/nba-engine.js';

export type DealStage = 'New' | 'Contacted' | 'Negotiation' | 'Won' | 'Lost';

export interface DealCompanyRef {
  id: string;
  name: string;
}

export interface DealContact {
  id: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  /** Компания контакта — источник унаследованной компании сделки. */
  companyRef?: DealCompanyRef | null;
}

export interface DealOwner {
  id: string;
  name?: string | null;
  email?: string | null;
}

export interface DealActivityItem {
  id: string;
  kind: string;
  actor: string;
  subject?: string | null;
  payload?: { body?: string } | null;
  createdAt: string;
  occurredAt?: string;
}

export interface DealListItem {
  id: string;
  workspaceId: string;
  title: string;
  amount: number | string;
  currency: 'RUB';
  stage: DealStage;
  contactId: string | null;
  companyId?: string | null;
  ownerId: string | null;
  tags: string[];
  nextActivityDue: string | null;
  lastActivityAt: string | null;
  createdAt: string;
  updatedAt?: string;
  contact?: DealContact | null;
  /** Компания сделки напрямую; может отсутствовать при заполненной у контакта. */
  company?: DealCompanyRef | null;
  owner?: DealOwner | null;
  /**
   * Привязки, подтянутые от соседа: их считает сервер общим правилом наследования.
   * Ключ — имя привязки (`company`), значение — что показать и через что она пришла.
   */
  inheritedLinks?: Record<string, InheritedLink> | undefined;
}

export interface DealDetail extends DealListItem {
  source?: string | null;
  activities: DealActivityItem[];
  notes?: string | null;
}

export interface ListDealsFilters {
  stage?: DealStage;
  companyId?: string;
  contactId?: string;
  /** Поиск по названию сделки и имени контакта — считает сервер. */
  q?: string;
  ownerId?: string;
  /** `due` — есть следующий шаг, `missing` — его нет. */
  activity?: 'due' | 'missing';
  /** Max results (1-200). Default: no limit (all deals). */
  limit?: number;
  /** Cursor for pagination: last item id from previous page. */
  cursor?: string;
}

export interface UpdateDealStageInput {
  toStage: DealStage;
  reason?: string;
}

export interface UpdateDealInput {
  title?: string;
  amount?: number;
  source?: string | null;
  nextActivityDue?: string | null;
  /** null — отвязать. Компания наследуется от контакта, если её не указали явно. */
  contactId?: string | null;
  companyId?: string | null;
  expectedUpdatedAt?: string;
}

export interface AddDealNoteInput {
  body: string;
}

export interface CreateDealInput {
  title: string;
  amount: number;
  stage?: DealStage;
  /** Привязки необязательны: сделку заводят и без контакта, и на компанию целиком. */
  contactId?: string;
  companyId?: string;
}

export interface DealStageChangedEvent {
  type: 'deal.stage.changed';
  workspaceId: string;
  dealId: string;
  fromStage: DealStage;
  toStage: DealStage;
}

export function getDealsQueryKey(
  workspaceId: string,
  filters: ListDealsFilters = {},
): EntityListKey {
  return entityListKey('deals', workspaceId, { ...filters });
}

export function getDealDetailQueryKey(workspaceId: string, dealId: string): readonly string[] {
  // Тот же корень, что и у списка: инвалидация префикса обновляет и карточку.
  return entityDetailKey('deals', workspaceId, dealId);
}

export async function listDeals(filters: ListDealsFilters = {}): Promise<DealListItem[]> {
  const search = new URLSearchParams();
  if (filters.stage !== undefined) {
    search.set('stage', filters.stage);
  }
  if (filters.companyId !== undefined) {
    search.set('companyId', filters.companyId);
  }
  if (filters.contactId !== undefined) {
    search.set('contactId', filters.contactId);
  }
  if (filters.q?.trim()) {
    search.set('q', filters.q.trim());
  }
  if (filters.ownerId !== undefined) {
    search.set('ownerId', filters.ownerId);
  }
  if (filters.activity !== undefined) {
    search.set('activity', filters.activity);
  }
  if (filters.limit !== undefined) {
    search.set('limit', String(filters.limit));
  }
  if (filters.cursor !== undefined) {
    search.set('cursor', filters.cursor);
  }

  const path = search.size > 0 ? `/deals?${search.toString()}` : '/deals';
  const res = await apiGet<{ data: DealListItem[] }>(path);
  return res.data;
}

export async function listDealsPaginated(
  filters: ListDealsFilters & { limit: number } = { limit: 50 },
): Promise<{ data: DealListItem[]; hasMore: boolean; nextCursor?: string | null }> {
  const search = new URLSearchParams();
  if (filters.stage !== undefined) search.set('stage', filters.stage);
  if (filters.companyId !== undefined) search.set('companyId', filters.companyId);
  if (filters.contactId !== undefined) search.set('contactId', filters.contactId);
  if (filters.q?.trim()) search.set('q', filters.q.trim());
  if (filters.ownerId !== undefined) search.set('ownerId', filters.ownerId);
  if (filters.activity !== undefined) search.set('activity', filters.activity);
  search.set('limit', String(filters.limit));
  if (filters.cursor !== undefined) search.set('cursor', filters.cursor);

  return apiGet<{ data: DealListItem[]; hasMore: boolean; nextCursor?: string | null }>(
    `/deals?${search.toString()}`,
  );
}

export async function createDeal(input: CreateDealInput): Promise<DealListItem> {
  return apiPost<DealListItem, CreateDealInput>('/deals', input);
}

export async function updateDealStage(
  dealId: string,
  input: UpdateDealStageInput,
): Promise<DealListItem> {
  return apiPatch<DealListItem, UpdateDealStageInput>(`/deals/${dealId}/stage`, input);
}

export async function updateDeal(dealId: string, input: UpdateDealInput): Promise<DealDetail> {
  return apiPatch<DealDetail, UpdateDealInput>(`/deals/${dealId}`, input);
}

export async function addDealNote(
  dealId: string,
  input: AddDealNoteInput,
): Promise<DealActivityItem> {
  return apiPost<DealActivityItem, AddDealNoteInput>(`/deals/${dealId}/notes`, input);
}

export async function getDealById(dealId: string): Promise<DealDetail> {
  return apiGet<DealDetail>(`/deals/${dealId}`);
}

export interface DealStageHistoryItem {
  id: string;
  fromStage: DealStage;
  toStage: DealStage;
  changedBy: string;
  reason?: string | null;
  createdAt: string;
}

export function getDealStageHistoryQueryKey(
  workspaceId: string,
  dealId: string,
): readonly string[] {
  return entityDetailKey('deals', workspaceId, dealId, 'stage-history');
}

export async function listDealStageHistory(dealId: string): Promise<DealStageHistoryItem[]> {
  return apiGet<DealStageHistoryItem[]>(`/deals/${dealId}/stage-history`);
}

export async function deleteDeal(dealId: string): Promise<void> {
  await apiDelete<void>(`/deals/${dealId}`);
}

export type DealSalesHealthSummary = DealSalesHealth;

export function getDealSalesHealthQueryKey(workspaceId: string, dealId: string): readonly string[] {
  return entityDetailKey('deals', workspaceId, dealId, 'sales-health');
}

export async function getDealSalesHealth(dealId: string): Promise<DealSalesHealthSummary> {
  return apiGet<DealSalesHealthSummary>(`/deals/${dealId}/sales-health`);
}

/** Heuristic win probability with its top explainable factors. */
export type DealWinProbabilitySummary = DealWinProbability;

/**
 * Own key root, deliberately outside the `['deals', …]` prefix: the stage
 * mutation awaits the invalidation refetches of that prefix, and a slow or
 * failing probability read must never block the mutation chain. The card
 * refreshes this query on stage-change WS events and on deal updates.
 */
export function getDealWinProbabilityQueryKey(
  workspaceId: string,
  dealId: string,
): readonly string[] {
  return ['deal-win-probability', workspaceId, dealId] as const;
}

export async function getDealWinProbability(dealId: string): Promise<DealWinProbabilitySummary> {
  return apiGet<DealWinProbabilitySummary>(`/deals/${dealId}/win-probability`);
}

/* ── Next best action (NBA shock package) ──────────────────────────────── */

/** One ranked recommendation of the pure NBA engine, as the API returns it. */
export interface DealNextStepAction {
  /** `nba-<dealId>-<rank>`; the server re-derives the action, so it can shift. */
  actionId: string;
  type: NbaActionType;
  reasonCodes: NbaReasonCode[];
  reasonParams: Record<string, string | number>;
  /** 0..100 heuristic strength of the recommendation. */
  confidence: number;
  /** Win probability right now, 0..100. */
  currentScore: number;
  /** Win probability after the action, from the same engine. */
  projectedScore: number;
  /** `projectedScore - currentScore`; 0 when the score cannot move. */
  projectedImpact: number;
  /** `server` actions run through the API; `navigate` ones belong to the card. */
  executeMode: NbaExecuteMode;
  meta: NbaActionMeta;
}

/** The single best recommendation, or none when nothing applies right now. */
export interface DealNextStep {
  action: DealNextStepAction | null;
  generatedAt: string;
}

export interface ExecuteDealNextStepInput {
  actionId: string;
  type: NbaActionType;
  /** Localized task/subject text; the server stores it as-is. */
  title?: string;
  /** ISO instant; omitted means the server's default horizon. */
  scheduledFor?: string;
  note?: string;
}

export interface DealNextStepExecutionResult {
  executed: true;
  type: NbaActionType;
  beforeScore: number;
  afterScore: number;
  meta: NbaActionMeta & { createdTaskId?: string; completedTasks?: number };
}

/** A what-if projection: pure calculation, nothing is persisted. */
export interface DealWhatIfProjection {
  currentScore: number;
  projectedScore: number;
  projectedImpact: number;
}

/**
 * Own key root, deliberately outside `['deals', …]`: the card is not part of
 * the detail payload, and a failing next-step read must not block a deal
 * mutation that awaits invalidation of that prefix.
 */
export function getDealNextStepQueryKey(workspaceId: string, dealId: string): readonly string[] {
  return ['deal-next-step', workspaceId, dealId] as const;
}

/** One projection per action type; `null` types never mount the query. */
export function getDealWhatIfQueryKey(
  workspaceId: string,
  dealId: string,
  type: NbaActionType | null,
): readonly string[] {
  return ['deal-what-if', workspaceId, dealId, type ?? 'none'] as const;
}

export async function getDealNextStep(dealId: string): Promise<DealNextStep> {
  return apiGet<DealNextStep>(`/deals/${dealId}/next-step`);
}

export async function executeDealNextStep(
  dealId: string,
  input: ExecuteDealNextStepInput,
): Promise<DealNextStepExecutionResult> {
  return apiPost<DealNextStepExecutionResult, ExecuteDealNextStepInput>(
    `/deals/${dealId}/next-step/execute`,
    input,
  );
}

export async function simulateDealWhatIf(
  dealId: string,
  type: NbaActionType,
): Promise<DealWhatIfProjection> {
  return apiPost<DealWhatIfProjection, { type: NbaActionType }>(
    `/deals/${dealId}/win-probability/what-if`,
    { type },
  );
}

/* ── Attention queue (NBA shock package, batch 4) ──────────────────────── */

/** One open deal of the "money at risk" queue, as the API ranks it. */
export interface DealAttentionQueueItem {
  dealId: string;
  title: string;
  /** Prisma `DealStage` name; the widget localizes it. */
  stage: string;
  /** Deal amount in whole currency units. */
  amount: number;
  /** Win probability, 0..100. */
  score: number;
  /** `amount × (1 − score / 100)`, rounded to whole rubles. */
  riskAmount: number;
  /** Signals currently firing, strongest first. */
  reasonCodes: NbaReasonCode[];
}

/**
 * Pipeline-wide key root, outside `['deals', …]` for the same reason as the
 * next-step key: a failing queue read must not block deal mutations awaiting
 * invalidation of the list prefix.
 */
export function getDealAttentionQueueQueryKey(
  workspaceId: string,
  limit: number,
): readonly ['deal-attention-queue', string, number] {
  return ['deal-attention-queue', workspaceId, limit] as const;
}

/** The response is a bare array: the queue is a ranking, not a paged list. */
export async function getDealsAttentionQueue(limit?: number): Promise<DealAttentionQueueItem[]> {
  const query = limit === undefined ? '' : `?limit=${limit}`;
  return apiGet<DealAttentionQueueItem[]>(`/deals/attention-queue${query}`);
}
